import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, ArrowUpRight, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function ProjectCard({ project, index, onOpen }) {
  const ref = useRef(null);
  const [style, setStyle] = useState({});

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setStyle({
      transform: `perspective(1000px) rotateY(${px * 8}deg) rotateX(${-py * 8}deg) translateY(-6px)`,
      transition: "transform 0.08s ease-out",
    });
  };

  const onLeave = () =>
    setStyle({ transform: "perspective(1000px) rotateY(0deg) rotateX(0deg)", transition: "transform 0.5s ease-out" });

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.12 }}
    >
      <button
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onClick={() => onOpen(project)}
        style={style}
        data-testid={`project-card-${project.id}`}
        className="group relative w-full text-left rounded-2xl border border-slate-800 bg-[#0D111D]/90 overflow-hidden transition-colors duration-300 hover:border-[#FF8800]/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8800]"
      >
        <div className="relative h-56 sm:h-64 overflow-hidden">
          {project.image ? (
            <img
              src={project.image}
              alt={project.name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
          ) : (
            <div className="w-full h-full bg-grid-tech flex items-center justify-center text-slate-600 font-mono-tech text-xs tracking-widest">
              LAUNCHLAB PROJECT
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D111D] via-transparent to-transparent" />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[linear-gradient(115deg,transparent_30%,rgba(255,255,255,0.08)_45%,transparent_60%)]" />
          <span className="absolute top-4 left-4 glass px-3 py-1 rounded-full font-mono-tech text-[10px] tracking-[0.18em] text-[#00B0FF] uppercase">
            {project.category || "Venture"}
          </span>
          <span className="absolute top-4 right-4 glass px-3 py-1 rounded-full font-mono-tech text-[10px] tracking-[0.14em] text-[#00E676] uppercase flex items-center gap-1.5">
            <BadgeCheck className="w-3 h-3" /> {project.completion_status || "Completed"}
          </span>
        </div>
        <div className="p-6 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl sm:text-2xl font-semibold text-white">{project.name}</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-2">{project.description}</p>
          </div>
          <div className="w-9 h-9 rounded-full glass flex items-center justify-center shrink-0 transition-all duration-300 group-hover:bg-[#FF8800] group-hover:border-[#FF8800]">
            <ArrowUpRight className="w-4 h-4 text-slate-300 transition-colors group-hover:text-[#06080E]" />
          </div>
        </div>
      </button>
    </motion.div>
  );
}

export function Projects({ projects }) {
  const [selected, setSelected] = useState(null);

  return (
    <section id="projects" data-testid="projects-section" className="relative py-20 sm:py-28 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(0,176,255,0.05),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-14"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">07 — Proof of Work</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="projects-headline">
            COMPLETED <span className="text-gradient-saffron">PROJECTS</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">
            Ideas transformed into real-world businesses and digital solutions.
          </p>
        </motion.div>

        {projects === null ? (
          <div className="flex justify-center py-20" data-testid="projects-loading">
            <div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="glass rounded-2xl p-14 text-center text-slate-400" data-testid="projects-empty">
            Completed projects will be showcased here soon.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {projects.map((p, i) => (
              <ProjectCard key={p.id} project={p} index={i} onOpen={setSelected} />
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent
          className="bg-[#0D111D] border-white/10 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto p-0"
          data-testid="project-detail-modal"
        >
          {selected && (
            <div>
              <div className="relative h-64 sm:h-80">
                {selected.image && (
                  <img src={selected.image} alt={selected.name} className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D111D] via-[#0D111D]/30 to-transparent" />
                <button
                  onClick={() => setSelected(null)}
                  data-testid="project-modal-close-button"
                  className="absolute top-4 right-4 w-9 h-9 rounded-full glass flex items-center justify-center text-slate-300 hover:text-white"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="absolute bottom-5 left-6 right-6">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="glass px-3 py-1 rounded-full font-mono-tech text-[10px] tracking-[0.18em] text-[#00B0FF] uppercase">
                      {selected.category}
                    </span>
                    <span className="glass px-3 py-1 rounded-full font-mono-tech text-[10px] tracking-[0.14em] text-[#00E676] uppercase flex items-center gap-1.5">
                      <BadgeCheck className="w-3 h-3" /> {selected.completion_status}
                    </span>
                  </div>
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl sm:text-3xl font-bold text-white text-left" data-testid="project-modal-title">
                      {selected.name}
                    </DialogTitle>
                  </DialogHeader>
                </div>
              </div>
              <div className="p-6 sm:p-8">
                <p className="text-base text-slate-300 leading-relaxed" data-testid="project-modal-description">
                  {selected.description}
                </p>
                {selected.deliverables && (
                  <div className="mt-7">
                    <p className="font-mono-tech text-[11px] tracking-[0.3em] text-[#FF8800] uppercase mb-4">
                      What LaunchLab Created
                    </p>
                    <ul className="grid sm:grid-cols-2 gap-3" data-testid="project-modal-deliverables">
                      {selected.deliverables.split("\n").filter(Boolean).map((d) => (
                        <li key={d} className="glass rounded-lg px-4 py-3 text-sm text-slate-200 flex items-center gap-2.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#FF8800] shrink-0" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {selected.url && (
                  <a
                    href={selected.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="project-modal-visit-link"
                    className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-sm transition-transform hover:scale-105"
                  >
                    Go to Website <ArrowUpRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
